import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Image, Table, Tag, Tooltip, message } from "antd";

import moment from "moment-timezone";
import { getBusinessTimeZone } from "../../../helpers/moment";

import PlanningHeaderForm from "./Components/PlanningHeaderForm";

import {
  momentDDMMYY,
  useDebounce,
  CapitalizeFirstWord,
  momentDateTime,
  getThumbnailUrl,
  DefaultNoImageFound,
  formatCurrency,
  getFullAwsUrl,
} from "../../../helpers/utils";
import { Form, Formik } from "formik";
import {
  PoductStartEditArray,
  PlaningHeaderValidationSchema,
  OrderStatusEnum,
} from "./orderTypes";
import {
  SpliteProductAPI,
  UpdateProductionDatesAPI,
  PrefillProductionPlanningAPI,
} from "../../Redux/orders";
import { fetchProductionPlanningQueryFn } from "./productionPlanningApis";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { useOutletContext, useParams } from "react-router-dom";
import { BtnLoader } from "../../../Components/Components";
import ConfirmSplitModal from "./Components/Modals/ConfirmSplitModal";
import FullScreenLoader from "../../Components/FullScreenLoader";
import { decryptByText } from "../../../helpers/bycrypt";
import CommonDeleteModal from "../../Components/Modals/CommonDeleteModal";
import MachineConflictModal from "./Components/Modals/MachineConflictModal";
import PlaningNotSaveWarning from "./Components/Modals/PlaningNotSaveWarning";
import {
  PagePermissionNameData,
  usePermissionsChecker,
} from "../../../helpers/permissionModule";
import NoPermissionPage from "../../Components/NoPermissionPage";
import { svg } from "../../../Assets/svg";
import EditOrderProductionData from "./Components/Modals/EditOrderProductionData";
import { EyeOutlined, ReloadOutlined, SyncOutlined } from "@ant-design/icons";
import ReloadPrifieldDataConfirmationModal from "./Components/Modals/ReloadPrifieldDataConfirmationModal";
import ProductionLogModal from "./Components/Modals/ProductionLogModal";
import CommonFilePreviewModal from "../../Components/CommonFilePreviewModal";
import { AttachedDrawings } from "../Quotations/QuotationDetails/ProductDetails";
import InstallationChecklistRequiredModal from "./Components/Modals/InstallationChecklistRequiredModal";
import DescriptionWithShowMore from "../../../helpers/DescriptionWithShowMore";

dayjs.extend(isoWeek);

const ImproveProductionPlaning = ({
  planning_details: propPlanningDetails,
}: any) => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const [previewFiles, setPreviewFiles] = useState<any>(null);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);

  const { productionPlanData, orderDetails } = useAppSelector(
    (state: RootState) => state.orders,
  );
  const planning_details =
    orderDetails?.planning_details || propPlanningDetails;

  const USER_PERMISSIONS = usePermissionsChecker(
    PagePermissionNameData?.ORDERS,
  );
  const updateProductionDateLoading = useAppSelector(
    (state: RootState) => state.loading.updateProductionDateLoading,
  );
  const getProductionPlanQueryResult = useQuery({
    queryKey: ["productionPlanning", id],
    queryFn: ({ signal }) =>
      fetchProductionPlanningQueryFn(id as string, signal),
    enabled: !!id,
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
    staleTime: 0,
  });
  const getProductionPlanLoading = getProductionPlanQueryResult.isLoading;
  const productionPlanQueryData = getProductionPlanQueryResult.data;
  const authLoading = useAppSelector(
    (state: RootState) => state.loading.authLoading,
  );
  const prefillProductionPlanningLoading = useAppSelector(
    (state: RootState) => state.loading.prefillProductionPlanningLoading,
  );
  const splitProductLoading = useAppSelector(
    (state: RootState) => state.loading.splitProductLoading,
  );
  const productionCategories =
    useAppSelector((state: RootState) => state.orders.productionCategories) ||
    [];

  const { setExpendProductListTable, expendProductListTable } =
    useOutletContext<any>();

  const isModifiedDatesRef = useRef<boolean>(false);
  // State management
  const [isModifiedDates, setIsModifiedDates] = useState(false);
  const [isEditDataModal, setIsEditDataModal] = useState<boolean>(false);
  const [editedData, setEditedData] = useState<any>({});
  const [isConfirmSplite, setIsConfirmSplite] = useState(false);
  const [splitData, setSplitData] = useState<any>(null);

  const [isConfirmCopy, setIsConfirmCopy] = useState(false);
  const [isDeleteItem, setIsDeleteItem] = useState(false);
  const [isDeleteData, setIsDeleteData] = useState<any>(null);

  const [isMachinConflict, setIsMachinConflict] = useState(false);
  const [isMachinConflctData, setIsMachinConflctData] = useState<any>(null);

  const [isWarrning, setIsWarrning] = useState(false);

  const [openPrefiedConfimation, setOpenPrefiedConfimation] = useState(false);

  const [openAddManufacturing, setOpenAddManufacturing] = useState(false);
  const [openAddManufacturingData, setOpenAddManufacturingData] = useState({
    productionid: null,
    splitId: null,
    today_date: null,
  });
  const [refreshData, setRefreshData] = useState(1);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  const handleManualRefresh = async () => {
    setIsManualRefreshing(true);
    try {
      await getProductionPlanQueryResult.refetch();
    } finally {
      setIsManualRefreshing(false);
    }
  };

  // Installation Checklist Restriction State
  const isChecklistRequired = Boolean(
    orderDetails?.installation_mark_up_required,
  );
  const isChecklistFilled =
    !isChecklistRequired ||
    Boolean(orderDetails?.is_installation_checklist_filled);

  const [isChecklistModalOpen, setIsChecklistModalOpen] = useState(false);

  useEffect(() => {
    if (!isChecklistFilled) {
      setIsChecklistModalOpen(true);
    }
  }, [isChecklistFilled]);

  // Sync React Query data into Redux so all existing selectors keep working
  useEffect(() => {
    if (productionPlanQueryData) {
      dispatch({
        type: "GET_ORDERS_PRODUCTION_PLAN",
        payload: productionPlanQueryData,
      });
    }
  }, [dispatch, productionPlanQueryData]);

  const handleSavePlaningDates = useCallback(
    (values: any) => {
      if (!isChecklistFilled) {
        setIsChecklistModalOpen(true);
        return;
      }
      const formatDateValue = (val: any) => {
        if (!val) return null;
        if (typeof val?.toISOString === "function") return val.toISOString();
        if (typeof val === "string") return dayjs(val).toISOString();
        return null;
      };

      const payload = {
        order_id: id,
        production_start_date: formatDateValue(values.production_start_date),
        production_completion_date: formatDateValue(
          values.production_completion_date,
        ),
        delivery_date: formatDateValue(values.delivery_date),
        installation_date: formatDateValue(values.installation_date),
      };

      const handleSuccessEditDates = () => {
        isModifiedDatesRef.current = false;
        setIsModifiedDates(false);
      };
      dispatch(UpdateProductionDatesAPI(payload, handleSuccessEditDates));
    },
    [id, dispatch, isChecklistFilled],
  );

  const handleDeleteItems = useCallback(() => {
    if (!isChecklistFilled) {
      setIsChecklistModalOpen(true);
      return;
    }
    const payload = [
      {
        items_id: isDeleteData?.item_id,
        splits: [{ _id: isDeleteData?._id, remove: 1 }],
      },
    ];
    const handleSuccess = () => {
      message.success("Production data deleted successfully!");
      setIsDeleteData(null);
      setIsDeleteItem(false);
    };

    dispatch(SpliteProductAPI(id, payload, handleSuccess));
  }, [dispatch, id, isDeleteData, isChecklistFilled]);

  const handleEdit = useCallback((record: any) => {
    setEditedData(record);
    setIsEditDataModal(true);
  }, []);

  const handleSplitQty = useCallback(
    (newSplitData: any) => {
      if (!isChecklistFilled) {
        setIsChecklistModalOpen(true);
        return;
      }
      setSplitData(newSplitData);
      setIsConfirmSplite(true);
    },
    [isChecklistFilled],
  );
  const handleCopyQty = useCallback(
    (newSplitData: any) => {
      if (!isChecklistFilled) {
        setIsChecklistModalOpen(true);
        return;
      }
      setSplitData(newSplitData);
      setIsConfirmSplite(true);
      setIsConfirmCopy(true);
    },
    [isChecklistFilled],
  );

  const bespokeItems = useMemo(
    () =>
      orderDetails?.bespoke_items || propPlanningDetails?.bespoke_items || [],
    [orderDetails, propPlanningDetails],
  );

  const bespokeColumns = useMemo(
    () => [
      {
        title: "Product",
        key: "product",
        render: (_: any, record: any) => {
          const itemImage = record.image
            ? getFullAwsUrl(record.image)
            : DefaultNoImageFound;
          return (
            <div className="d-flex align-items-center gap-2">
              <Image
                src={itemImage}
                alt={record.name}
                className="rounded-2 object-fit-cover border w-100 h-100"
                rootClassName="img-45"
                fallback={DefaultNoImageFound}
                preview={{
                  mask: <EyeOutlined />,
                }}
              />
              <div>
                <h6 className="fw-semibold mb-0 text-dark fs-6">
                  {record?.name || "Bespoke Item"}
                </h6>
                {record?.description && (
                  <small
                    className="text-muted d-block"
                    style={{ fontSize: "0.85rem" }}
                  >
                    {record.description}
                  </small>
                )}
              </div>
            </div>
          );
        },
      },
      {
        title: "Supplier",
        dataIndex: "supplier_name",
        key: "supplier_name",
        render: (val: string) => val || "-",
      },
      {
        title: "Supplier Quote No",
        dataIndex: "supplier_quote_no",
        key: "supplier_quote_no",
        render: (val: string) => val || "-",
      },
      {
        title: "Qty",
        dataIndex: "qty",
        key: "qty",
        align: "center" as const,
        render: (val: number) => val ?? 1,
      },
    ],
    [orderDetails],
  );

  const columns = useMemo(
    () => [
      {
        title: "Product",
        onCell: (record: any) => ({ rowSpan: record.rowSpan }),
        render: (_: any, record: any) => (
          <>
            <div className="d-flex gap-2">
              <Image
                src={
                  record?.product?.image &&
                  record?.product?.image !== DefaultNoImageFound
                    ? getThumbnailUrl(record?.product?.image)
                    : DefaultNoImageFound
                }
                alt={record?.product?.name}
                className="rounded-2 object-fit-cover border w-100 h-100"
                rootClassName="img-45"
                fallback={DefaultNoImageFound}
                preview={{
                  mask: <EyeOutlined />, // 👈 custom icon here
                }}
              />
              <div>
                <div className="mb-1">
                  <p
                    className="mb-1"
                    style={{
                      fontSize: "12px",
                      color:
                        record?.item_type == "bom_part"
                          ? "#EA7E58"
                          : record?.item_type == "component"
                          ? "#A756AC"
                          : "#58BF6E",
                    }}
                  >
                    {record?.item_type === "bom_part"
                      ? "Part"
                      : record?.item_type === "component"
                      ? "Component"
                      : record?.item_type === "advertisement_sign"
                      ? "Advertisement Sign"
                      : "Variant"}
                  </p>
                  {/* <Tag
                    className="px-1 rounded-3 m-0 mw-100 text-wrap"
                    style={{ fontSize: "12px" }}
                    color={
                      record?.item_type == "bom_part"
                        ? "#EA7E58"
                        : record?.item_type == "component"
                        ? "#A756AC"
                        : "#58BF6E"
                    }
                  >
                    {record?.item_type == "bom_part"
                      ? "Part"
                      : record?.item_type == "component"
                      ? "Component"
                      : "Variant"}
                  </Tag> */}
                </div>
                <h6 className="fw-semibold mb-0 text-dark fs-6 mb-1">
                  {["bom_part", "component"]?.includes(record?.item_type)
                    ? record?.name
                    : record?.variant_name ||
                      record?.product?.variants?.variant_name ||
                      record?.product?.product_name ||
                      "-"}
                </h6>
                <h6
                  className="fw-semibold mb-0 text-dark"
                  style={{ fontSize: "12px" }}
                >
                  Qty - {record?.product?.variant_qty}
                </h6>
              </div>
            </div>
            <small className="text-muted fw-normal fs-8 d-inline-block mt-1 device-w-fix w-100">
              <DescriptionWithShowMore
                description={record?.product?.description}
                lines={4}
              />
              {record?.product?.variants?.max_load_per_bay && (
                <div className="lh-tight">
                  Max Load per Bay: {record?.product?.variants.max_load_per_bay}
                </div>
              )}
              {record?.product?.variants?.max_load_per_pair && (
                <div className="lh-tight">
                  Max Load per Pair:{" "}
                  {record?.product?.variants.max_load_per_pair}
                </div>
              )}
              {record?.product?.variants?.max_load_per_shelf && (
                <div className="lh-tight">
                  Max Load per Shelf:{" "}
                  {record?.product?.variants.max_load_per_shelf}
                </div>
              )}
              <AttachedDrawings
                drawings={
                  record?.drawings || record?.product?.variants?.drawings || []
                }
                setPreviewModal={setPreviewModalOpen}
                setPreviewFiles={setPreviewFiles}
                setPreviewIndex={setPreviewIndex}
                layout="table"
              />
            </small>
          </>
        ),
        width: "16%",
      },
      {
        title: "Qty",
        dataIndex: "qty",
        width: "5%",
        render: (_: any, record: any) => {
          return USER_PERMISSIONS?.MODIFY ? (
            <div>
              <div>{record.qty || 0}</div>
              <div className="fs-8 text-secondary mb-1">
                {record?.labelInQuotation || "Qty"}
              </div>
              {PoductStartEditArray?.includes(orderDetails?.order_status) ? (
                <>
                  {(record.qty || 0) > 1 && (record?.pending_qty || 0) > 1 ? (
                    <span
                      className="text-decoration-underline fw-bold me-1"
                      role="button"
                      onClick={() => handleSplitQty(record)}
                    >
                      {" "}
                      Split
                    </span>
                  ) : (
                    ""
                  )}
                  <span
                    className="text-decoration-underline fw-bold"
                    role="button"
                    onClick={() => handleCopyQty(record)}
                  >
                    {" "}
                    Copy
                  </span>
                </>
              ) : (
                ""
              )}
            </div>
          ) : (
            <div>
              <div>{record.qty || 0}</div>
              <div className="fs-8 text-secondary">
                {record?.labelInQuotation || "Qty"}
              </div>
            </div>
          );
        },
      },
      {
        title: "Unit",
        dataIndex: "unit",
        render: (unit: any) => <span>{unit || "-"}</span>,
        width: "4%",
      },
      {
        title: "Department",
        dataIndex: "department_id",
        width: "9%",
        render: (_: any, record: any) => {
          const deptIdStr = String(
            record?.department_id?._id || record?.department_id || "",
          );
          const deptName =
            record?.department_id?.name ||
            (typeof record?.department_id === "string" && record?.department_id
              ? record?.department_id
              : "-");
          const allDrawings =
            record?.drawings || record?.product?.variants?.drawings || [];

          const deptDrawings = deptIdStr
            ? allDrawings.filter((d: any) => {
                if (!d) return false;
                if (Array.isArray(d.departments) && d.departments.length > 0) {
                  return d.departments.some((dept: any) => {
                    const id =
                      typeof dept === "object"
                        ? String(dept?._id || dept?.id || "")
                        : String(dept);
                    return id === deptIdStr;
                  });
                }
                return false;
              })
            : [];

          return (
            <div className="d-flex flex-column gap-1">
              <span>{deptName}</span>
              {deptDrawings.length > 0 && (
                <div className="mt-1">
                  <AttachedDrawings
                    drawings={deptDrawings}
                    setPreviewModal={setPreviewModalOpen}
                    setPreviewFiles={setPreviewFiles}
                    setPreviewIndex={setPreviewIndex}
                    layout="department"
                  />
                </div>
              )}
            </div>
          );
        },
      },
      {
        title: "Machine",
        dataIndex: "machine_id",
        width: "11%",
        render: (_: any, record: any) => (
          <span>{record?.machine_id?.name || record?.machine_id || "-"}</span>
        ),
      },
      {
        title: "Employee",
        dataIndex: "employee_ids",
        width: "13%",
        render: (_: any, record: any) => (
          <span>
            {record?.employee_ids?.length > 0
              ? record?.employee_ids?.map((emp: any, index: number) => {
                  const fullName = `${CapitalizeFirstWord(
                    decryptByText(emp?.first_name),
                  )} ${CapitalizeFirstWord(decryptByText(emp?.last_name))}`;
                  return (
                    <span key={index}>
                      {fullName || "-"}
                      {index < record?.employee_ids?.length - 1 ? ", " : ""}
                    </span>
                  );
                })
              : "-"}
          </span>
        ),
      },
      {
        title: "Start Date",
        dataIndex: "start_at",
        width: "9%",
        render: (_: any, record: any) => (
          <span>{record?.start_at ? momentDDMMYY(record?.start_at) : "-"}</span>
        ),
      },
      {
        title: "End Date",
        dataIndex: "end_at",
        width: "9%",
        render: (_: any, record: any) => (
          <span>{record?.end_at ? momentDDMMYY(record?.end_at) : "-"}</span>
        ),
      },
      {
        title: "Manufactured Qty",
        dataIndex: "manufactured_qty",
        width: "6%",
        render: (_: any, record: any) => (
          <div>
            <div>{record.manufactured_qty || 0}</div>
            <div className="fs-8 text-secondary">
              {record?.labelInQuotation || "Qty"}
            </div>
            {[
              OrderStatusEnum.PRODUCTION_PLANNING,
              OrderStatusEnum.PRODUCTION_STARTED,
            ].includes(orderDetails?.order_status) && (
              <div
                className="text-decoration-underline fw-bold cursor-pointer"
                onClick={() => {
                  setOpenAddManufacturing(true);
                  setOpenAddManufacturingData({
                    productionid: record?.planing_id,
                    splitId: record?._id,
                    today_date: record?.start_at,
                  });
                }}
              >
                Add
              </div>
            )}
          </div>
        ),
      },
      {
        title: "Pending Qty",
        dataIndex: "pending_qty",
        width: "6%",
        render: (_: any, record: any) => (
          <div>
            <div>{record.pending_qty || 0}</div>
            <div className="fs-8 text-secondary">
              {record?.labelInQuotation || "Qty"}
            </div>
          </div>
        ),
      },
      {
        title: "Status",
        dataIndex: "status",
        width: "8%",
        render: (_: any, record: any) => {
          const statusClassMap = {
            started: "designPendingStatus",
            completed: "approvedStatus",
            scheduled: "draftStatus",
          };

          const displayLabel =
            record?.status === "init"
              ? ""
              : record?.status === "started"
              ? "Started"
              : record?.status === "completed"
              ? "Completed"
              : "Scheduled";

          return displayLabel == "" ? (
            <>-</>
          ) : (
            <span
              className={`d-inline-flex align-items-center billStatus ${
                statusClassMap[record?.status as keyof typeof statusClassMap] ||
                ""
              }`}
            >
              {svg.dotIcon}
              {displayLabel}
            </span>
          );
        },
      },
      ...([
        OrderStatusEnum.ORDER_CREATED,
        OrderStatusEnum.PRODUCTION_STARTED,
        OrderStatusEnum.PRODUCTION_PLANNING,
      ]?.includes(orderDetails?.order_status)
        ? [
            {
              title: "Action",
              width: "7%",
              render: (_: any, record: any) => (
                <div className="d-flex align-items-center gap-2">
                  {USER_PERMISSIONS?.MODIFY ? (
                    <span
                      className="svg-26 cursor-pointer"
                      onClick={() => {
                        if (
                          isModifiedDatesRef?.current ||
                          !planning_details?.production_start_date
                        ) {
                          setIsWarrning(true);
                        } else {
                          handleEdit(record);
                        }
                      }}
                    >
                      {svg?.tableEditIcon}
                    </span>
                  ) : (
                    ""
                  )}

                  {USER_PERMISSIONS?.REMOVE &&
                  productionPlanData?.length > 1 ? (
                    <span
                      className="svg-26 cursor-pointer"
                      onClick={() => {
                        setIsDeleteData(record);
                        setIsDeleteItem(true);
                      }}
                    >
                      {svg?.tableDeleteIcon}
                    </span>
                  ) : (
                    ""
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [
      USER_PERMISSIONS,
      orderDetails,
      productionPlanData,
      handleSplitQty,
      handleCopyQty,
      handleEdit,
    ],
  );

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreen = () => {
      setIsMobile(window.innerWidth <= 1024); // iPad + Mobile
    };

    checkScreen();
    window.addEventListener("resize", checkScreen);

    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  const handlePrefiedRefresh = () => {
    if (id) {
      dispatch(
        PrefillProductionPlanningAPI(id, () => {
          setOpenPrefiedConfimation(false);
          queryClient.invalidateQueries({
            queryKey: ["productionPlanning", id],
          });
        }),
      );
    }
  };
  return USER_PERMISSIONS?.READ ? (
    <div>
      {expendProductListTable ? (
        ""
      ) : (
        <>
          {/* 1. Header */}
          {(isModifiedDates || !planning_details?.production_start_date) &&
          USER_PERMISSIONS?.MODIFY ? (
            <Formik
              initialValues={{
                production_start_date: planning_details?.production_start_date
                  ? dayjs(planning_details?.production_start_date)
                  : null,
                production_completion_date:
                  planning_details?.production_completion_date
                    ? dayjs(planning_details?.production_completion_date)
                    : null,
                delivery_date: planning_details?.delivery_date
                  ? dayjs(planning_details?.delivery_date)
                  : null,
                installation_date: planning_details?.installation_date
                  ? dayjs(planning_details?.installation_date)
                  : null,
                installation_planning: orderDetails?.installation_planning
                  ? true
                  : false,
              }}
              validationSchema={PlaningHeaderValidationSchema}
              onSubmit={handleSavePlaningDates}
              enableReinitialize
            >
              {({
                values,
                handleSubmit,
                errors,
                touched,
                setFieldValue,
              }: any) => {
                return (
                  <Form>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h4 className="fw-bold mb-0">Production Planning</h4>
                      {isModifiedDates ? (
                        <span
                          role="button"
                          className="text-decoration-underline fw-bold h-auto"
                          onClick={() => {
                            isModifiedDatesRef.current = false;
                            setIsModifiedDates(false);
                          }}
                          // loading={saving}
                        >
                          Cancel
                        </span>
                      ) : (
                        ""
                      )}
                    </div>

                    {/* 2. Dates */}
                    <div className="bg-white p-3 p-md-4 rounded-3 shadow-sm mb-4">
                      <div className="d-lg-flex align-items-start gap-3">
                        <div className="w-100 mb-lg-0 mb-3">
                          <PlanningHeaderForm
                            values={values}
                            setFieldValue={setFieldValue}
                            errors={errors}
                            touched={touched}
                            orderDetails={orderDetails}
                          />
                        </div>
                        {USER_PERMISSIONS?.MODIFY ? (
                          <div className="text-center" style={{ flex: 0 }}>
                            <label className="custom-label mb-1 ff opacity-0 d-lg-block d-none">
                              Hidden Text
                            </label>
                            <button
                              type="submit"
                              className="btn btn-primary btn-lg orderSaveBtn"
                              onClick={handleSubmit}
                              // loading={saving}
                              disabled={updateProductionDateLoading}
                            >
                              {updateProductionDateLoading ? (
                                <BtnLoader />
                              ) : (
                                "SAVE"
                              )}
                            </button>
                          </div>
                        ) : (
                          ""
                        )}
                      </div>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h4 className="fw-bold mb-0">Production Planning</h4>
                {USER_PERMISSIONS?.MODIFY &&
                orderDetails?.order_status != OrderStatusEnum?.CLOSED ? (
                  <span
                    role="button"
                    className="text-decoration-underline fw-bold h-auto"
                    onClick={() => {
                      setIsModifiedDates(true);
                      isModifiedDatesRef.current = true;
                    }}
                    // loading={saving}
                  >
                    Modify
                  </span>
                ) : (
                  ""
                )}
              </div>
              <div className="card border-0 shadow-sm p-md-4 p-3 rounded-4 bg-white mb-4">
                <div className="row gy-md-4 gy-3">
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="fs-6 fw-normal text-secondary d-block mb-1">
                      Production Start Week
                    </label>
                    <span className="fw-semibold text-dark fs-6">
                      {planning_details?.production_start_date
                        ? `Week ${moment(
                            planning_details?.production_start_date,
                          )
                            .tz(getBusinessTimeZone())
                            .isoWeek()}`
                        : "-"}
                    </span>
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="fs-6 fw-normal text-secondary d-block mb-1">
                      Production Completion Date
                    </label>
                    <span className="fw-semibold text-dark fs-6">
                      {planning_details?.production_completion_date
                        ? momentDDMMYY(
                            planning_details?.production_completion_date,
                          )
                        : "-"}
                    </span>
                  </div>
                  <div className="col-lg-3 col-md-4 col-sm-6">
                    <label className="fs-6 fw-normal text-secondary d-block mb-1">
                      Delivery Date
                    </label>
                    <span className="fw-semibold text-dark fs-6">
                      {planning_details?.delivery_date
                        ? momentDDMMYY(planning_details?.delivery_date)
                        : "-"}
                    </span>
                  </div>
                  {orderDetails?.installation_planning ? (
                    <div className="col-lg-3 col-md-4 col-sm-6">
                      <label className="fs-6 fw-normal text-secondary d-block mb-1">
                        Installation Date
                      </label>
                      <span className="fw-semibold text-dark fs-6">
                        {planning_details?.installation_date
                          ? momentDDMMYY(planning_details?.installation_date)
                          : "-"}
                      </span>
                    </div>
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* 3. Grouped Tables */}
      {openAddManufacturing && (
        <ProductionLogModal
          visible={openAddManufacturing}
          setIsModalVisible={setOpenAddManufacturing}
          onClose={() => {
            setOpenAddManufacturing(false);
            setOpenAddManufacturingData({
              productionid: null,
              splitId: null,
              today_date: null,
            });
          }}
          productionId={openAddManufacturingData.productionid}
          splitsId={openAddManufacturingData.splitId}
          today_date={openAddManufacturingData.today_date}
          is_available_qty={true}
        />
      )}
      <div className="d-flex flex-column gap-3">
        <div className="d-flex align-items-center justify-content-between">
          <h4 className="fw-bold mb-0">Products List</h4>
          <div className="d-flex align-items-center gap-4">
            {[
              OrderStatusEnum.ORDER_CREATED,
              OrderStatusEnum.PRODUCTION_STARTED,
              OrderStatusEnum.PRODUCTION_PLANNING,
            ]?.includes(orderDetails?.order_status) ? (
              <Tooltip title="Reassign Data">
                <span
                  className="d-flex align-items-center gap-2 fw-semibold"
                  role="button"
                  onClick={() => setOpenPrefiedConfimation(true)}
                >
                  <span className="svg-20 d-flex align-items-center justify-content-center">
                    <SyncOutlined
                      spin={prefillProductionPlanningLoading}
                      className="ms-1"
                    />
                  </span>{" "}
                  Reassign
                </span>
              </Tooltip>
            ) : (
              ""
            )}

            <span
              className="d-flex align-items-center gap-2 fw-semibold"
              role="button"
              onClick={() => setExpendProductListTable(!expendProductListTable)}
            >
              <span className="svg-20">
                {expendProductListTable ? svg?.collapseIcon : svg?.expandIcon}
              </span>{" "}
              {expendProductListTable ? "Collapse" : "Expand"}
            </span>
            <div className="d-flex align-items-center justify-content-between gap-2 bg-white rounded border fs-6 fs-6-mob gap-2 order-sm-3 order-2">
              <Tooltip title="Refresh Production Planning Data">
                <Button
                  type="text"
                  icon={<ReloadOutlined spin={isManualRefreshing} />}
                  onClick={handleManualRefresh}
                  className="btn btn-primary btn-36 d-flex align-items-center justify-content-center"
                />
              </Tooltip>
            </div>
          </div>
        </div>

        {getProductionPlanLoading ? (
          <div className="card border-0 shadow-sm p-4 bg-white rounded-4">
            <FullScreenLoader />
          </div>
        ) : (
          (() => {
            // Group items and extract unique category snapshots from items
            const categorizedData: { [key: string]: any[] } = {};
            const itemCategories: { [key: string]: any } = {};
            const otherItems: any[] = [];

            (productionPlanData || []).forEach((item: any) => {
              const catSnap = item?.categories_snapshot;
              const catId = catSnap?._id?.toString();

              if (catId) {
                if (!categorizedData[catId]) {
                  categorizedData[catId] = [];
                  itemCategories[catId] = catSnap;
                }
                categorizedData[catId].push(item);
              } else {
                otherItems.push(item);
              }
            });

            // Re-calculate rowSpan for each category group (sorted: Component, Part, Variant)
            const prepareDataSource = (data: any[]) => {
              const getItemTypeRank = (type: string) => {
                if (type === "component") return 1;
                if (type === "bom_part") return 2;
                return 3;
              };

              const sortedData = [...(data || [])].sort((a, b) => {
                const rankA = getItemTypeRank(a?.item_type);
                const rankB = getItemTypeRank(b?.item_type);
                return rankA - rankB;
              });

              const itemGroups: { [key: string]: any[] } = {};
              sortedData.forEach((row) => {
                const itemId = row.item_id?.toString();
                if (!itemGroups[itemId]) itemGroups[itemId] = [];
                itemGroups[itemId].push(row);
              });

              const dataSource: any[] = [];
              sortedData.forEach((row) => {
                const itemId = row.item_id?.toString();
                const splits = itemGroups[itemId];
                if (!splits) return;
                const index = splits.findIndex((s) => s.key === row.key);
                dataSource.push({
                  ...row,
                  rowSpan: index === 0 ? splits.length : 0,
                });
              });
              return dataSource;
            };

            const renderTable = (data: any[], title: string) => {
              return (
                <div key={title} className="mb-4">
                  <h5 className="fw-bold mb-3">{title}</h5>
                  <div
                    className={`card page-table-card variantsTableCard border-0 shadow rounded-4 flex-fill overflow-hidden ${
                      expendProductListTable ? "expandedTable" : ""
                    }`}
                  >
                    <div className="card-body overflow-y-auto h-100 p-0">
                      <Table
                        columns={columns}
                        scroll={isMobile ? { x: "max-content" } : undefined}
                        dataSource={prepareDataSource(data)}
                        rowKey={(record) => record?.key || record?._id}
                        pagination={false}
                        bordered
                        rowClassName={() => "align-top"}
                        className="variant-edit-table productionListTable bg-white"
                        size="middle"
                        onRow={(record) => ({
                          onDoubleClick: () => {
                            if (
                              PoductStartEditArray?.includes(
                                orderDetails?.order_status,
                              )
                            ) {
                              if (
                                isModifiedDates ||
                                !planning_details?.production_start_date
                              ) {
                                setIsWarrning(true);
                              }
                            }
                          },
                        })}
                      />
                    </div>
                  </div>
                </div>
              );
            };

            // Use categories we found in the items themselves
            const categoryIdsPresent = Object.keys(itemCategories);

            return (
              <>
                {categoryIdsPresent.map((catId) => {
                  const items = categorizedData[catId];
                  const cat = itemCategories[catId];
                  const title = `${
                    cat?.name || "Unknown Category"
                  } Products List`;
                  return renderTable(items, title);
                })}
                {otherItems.length > 0 &&
                  renderTable(otherItems, "Other Products List")}
                {bespokeItems.length > 0 && (
                  <div className="mb-4 mt-3">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <h4 className="fw-bold mb-0">
                        Bespoke / Bought-In Items List
                      </h4>
                    </div>
                    <div
                      className={`card page-table-card variantsTableCard border-0 shadow rounded-4 flex-fill overflow-hidden ${
                        expendProductListTable ? "expandedTable" : ""
                      }`}
                    >
                      <div className="card-body overflow-y-auto h-100 p-0">
                        <Table
                          columns={bespokeColumns}
                          dataSource={bespokeItems}
                          rowKey={(record: any, idx?: number) =>
                            String(
                              record?._id ||
                                record?.bespoke_item_id ||
                                idx ||
                                0,
                            )
                          }
                          pagination={false}
                          bordered
                          size="middle"
                          className="variant-edit-table productionListTable bg-white"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            );
          })()
        )}
      </div>
      {isConfirmSplite && (
        <ConfirmSplitModal
          visible={isConfirmSplite}
          setIsModalVisible={setIsConfirmSplite}
          onClose={() => {
            setIsConfirmSplite(false);
            setSplitData(null);
            setIsConfirmCopy(false);
          }}
          isCopy={isConfirmCopy}
          handleSucess={() => {
            if (isConfirmCopy) {
              const copyPayload = [
                {
                  items_id: splitData?.item_id,
                  splits: [
                    {
                      _id: splitData?._id,
                      variant_id: splitData?.variant_id,
                      qty: splitData?.qty,
                      pending_qty: splitData?.pending_qty,
                      department_id: splitData?.department_id?._id,
                      machine_id: splitData?.machine_id?._id,
                      employee_ids:
                        splitData?.employee_ids?.length > 0
                          ? splitData?.employee_ids?.map((emp: any) =>
                              emp?._id ? emp?._id : "",
                            )
                          : [],
                      start_at: splitData?.start_at,
                      end_at: splitData?.end_at,
                      manufactured_qty: splitData?.manufactured_qty,
                      status: splitData?.status,
                    },
                    {
                      after_splits_id: splitData?._id,
                      variant_id: splitData?.variant_id,
                      qty: splitData?.qty,
                      pending_qty: splitData?.qty,
                      department_id: null,
                      machine_id: null,
                      employee_ids: [],
                      start_at: null,
                      end_at: null,
                      manufactured_qty: 0,
                      status: "init",
                    },
                  ],
                },
              ];
              const successSplit = () => {
                message.success("Product duplicate successfully!");
                setIsConfirmSplite(false);
                setSplitData(null);
                setIsConfirmCopy(false);
                // Refresh data
              };
              dispatch(SpliteProductAPI(id, copyPayload, successSplit));
            } else {
              const firstQty = Math.ceil(splitData?.pending_qty / 2);
              const secondQty = splitData?.pending_qty - firstQty;
              const newQty = splitData?.qty - secondQty;
              const splitePayload = [
                {
                  items_id: splitData?.item_id,
                  splits: [
                    {
                      _id: splitData?._id,
                      variant_id: splitData?.variant_id,
                      qty: newQty,
                      pending_qty: firstQty,
                      department_id: splitData?.department_id?._id,
                      machine_id: splitData?.machine_id?._id,
                      employee_ids:
                        splitData?.employee_ids?.length > 0
                          ? splitData?.employee_ids?.map((emp: any) =>
                              emp?._id ? emp?._id : "",
                            )
                          : [],
                      start_at: splitData?.start_at,
                      end_at: splitData?.end_at,
                      manufactured_qty: splitData?.manufactured_qty,
                      status: splitData?.status,
                    },
                    {
                      after_splits_id: splitData?._id,
                      variant_id: splitData?.variant_id,
                      qty: secondQty,
                      pending_qty: secondQty,
                      department_id: splitData?.department_id?._id,
                      machine_id: splitData?.machine_id?._id,
                      employee_ids:
                        splitData?.employee_ids?.length > 0
                          ? splitData?.employee_ids?.map((emp: any) =>
                              emp?._id ? emp?._id : "",
                            )
                          : [],
                      start_at: null,
                      end_at: null,
                      manufactured_qty: 0,
                      status: "init",
                    },
                  ],
                },
              ];
              const successSplit = () => {
                message.success("Product split successfully!");
                setIsConfirmSplite(false);
                setSplitData(null);
                setIsConfirmCopy(false);
                // Refresh data
              };
              dispatch(SpliteProductAPI(id, splitePayload, successSplit));
            }
          }}
        />
      )}
      {openPrefiedConfimation && (
        <ReloadPrifieldDataConfirmationModal
          visible={openPrefiedConfimation}
          setIsModalVisible={setOpenPrefiedConfimation}
          onClose={() => {
            setOpenPrefiedConfimation(false);
          }}
          handleSucess={() => {
            handlePrefiedRefresh();
          }}
        />
      )}
      {isDeleteItem && (
        <CommonDeleteModal
          visible={isDeleteItem}
          setIsModalVisible={setIsDeleteItem}
          handleDelete={handleDeleteItems}
          title={"Remove Product From This List, Are You Sure?"}
          description={
            "Removing this Product will delete it from this list. Please confirm before proceeding."
          }
          setIsDeleteId={setIsDeleteData}
          isLoading={splitProductLoading}
          buttonName="REMOVE"
        />
      )}
      {isMachinConflict && (
        <MachineConflictModal
          visible={isMachinConflict}
          setIsModalVisible={setIsMachinConflict}
          onClose={() => {
            setIsMachinConflict(false);
            setIsMachinConflctData(null);
          }}
          isMachinConflctData={isMachinConflctData}
        />
      )}
      {isWarrning && (
        <PlaningNotSaveWarning
          visible={isWarrning}
          setIsModalVisible={() => {
            setIsWarrning(false);
          }}
          isWarrningDate={false}
        />
      )}
      {/* {isShowErrorModal && (
        <ShowRowEditModal
          visible={isShowErrorModal}
          setIsModalVisible={setIsShowErrorModal}
          errorData={rowEditError}
        />
      )} */}
      {isEditDataModal && (
        <EditOrderProductionData
          visible={isEditDataModal}
          setIsModalVisible={setIsEditDataModal}
          isEditData={editedData}
          setIsEditData={setEditedData}
          setIsMachinConflctData={setIsMachinConflctData}
          setIsMachinConflict={setIsMachinConflict}
          productionData={planning_details}
          onChecklistRequired={() => setIsChecklistModalOpen(true)}
        />
      )}
      {previewModalOpen && (
        <CommonFilePreviewModal
          visible={previewModalOpen}
          onClose={() => {
            setPreviewModalOpen(false);
            setPreviewFiles([]);
            setPreviewIndex(0);
          }}
          files={previewFiles}
          activeIndex={previewIndex}
          onActiveIndexChange={setPreviewIndex}
        />
      )}
      <InstallationChecklistRequiredModal
        open={isChecklistModalOpen}
        onClose={() => setIsChecklistModalOpen(false)}
      />
    </div>
  ) : authLoading ? (
    <FullScreenLoader />
  ) : (
    <NoPermissionPage />
  );
};

export default ImproveProductionPlaning;
